import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { AppConfig } from './model/app-config.model';
import { BaseMapConfig } from './model/basemap-config.model';
import { LayerContainerConfig } from './model/layercontainer-config.model';
import { ListViewConfig } from './model/listview-config.model';
import { Arbeitsbereich, ArbeitsbereichConfig } from './model/arbeitsbereich-config.model';
import { SearchConfig } from './model/search-config.model';
import { MainMapConfig } from './model/mainmap-config.model';
import { DetailViewConfig, DetailViewForm } from './model/detailview-config.model';
import { LayerConfig } from './model/layer-config.model';
import { GeoPortaleConfig } from './model/geoportale-config.model';
import { LayerListConfig, LayerListFunctionConfig } from './model/layerlist-config.model';
import { RelationshipTableConfig } from './model/relationship-config.model';
import { Environment } from '../../environments/environment.model';
import { IdentifyConfig } from './model/identify-config.model';
import { LAYER_LIST_FUNCTION_CONFIG } from '../../assets/config/shared/layer-list.config';
import { LAYER_LIST_CONFIG } from '../../assets/config/shared/layer-list.config';
import { SEARCH_CONFIG } from '../../assets/config/shared/search.config';
import { RELATIONSHIP_TABLE_CONFIG } from '../../assets/config/shared/relationship.config';
import { MAIN_MAP_CONFIG } from '../../assets/config/shared/main-map.config';
import { LIST_VIEW_CONFIG } from '../../assets/config/shared/list-view.config';
import { LAYER_CONTAINER_CONFIG } from '../../assets/config/shared/layer-container.config';
import { GEO_PORTAL_CONFIG } from '../../assets/config/shared/geo-portal.config';
import { BASE_MAP_CONFIG } from '../../assets/config/shared/base-map.config';
import { DETAIL_VIEW_CONFIG } from '../../assets/config/shared/detail-view.config';
import { ARBEITSBEREICH_CONFIG } from '../../assets/config/shared/arbeitsbereich.config';
import { IDENTIFY_CONFIG } from '../../assets/config/shared/identify.config';
import { APP_CONFIG } from '../../assets/config/shared/app.config';
import { CONSTANTS } from './constants.module';
import { ENVIRONMENT } from '../../environments/environment';
import Enums from '../shared/enum/enums';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class ConfigService
 * @version: 0.0.1
 * @since September 2017
 * @description The ConfigService is used during the app bootstrapping phase.
 * It's main goal is to load all JSON configuration files into the app and provide them for
 * global usage.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  /* Initialisiere alle configs mit Type */
  private readonly _translationFiles: any;
  private readonly _appConfig: AppConfig;
  private readonly _arbeitsbereichConfig: ArbeitsbereichConfig;
  readonly _identifyConfig: IdentifyConfig;
  private readonly _baseMapConfig: BaseMapConfig;
  private readonly _detailViewConfig: DetailViewConfig;
  private readonly _layerContainerConfig: LayerContainerConfig;
  private readonly _layerListConfig: LayerListConfig;
  private readonly _layerListFunctionConfig: LayerListFunctionConfig;
  private readonly _listViewConfig: ListViewConfig;
  private readonly _mainMapConfig: MainMapConfig;
  private readonly _relationshipTablesConfig: RelationshipTableConfig;
  private readonly _searchConfig: SearchConfig;
  private _rolesEnvironment: string;
  private readonly _geoPortaleConfig: GeoPortaleConfig;

  private _CONST: any;

  readonly environment: Environment;

  constructor(
    @Inject(HttpClient) private _httpClientService: HttpClient,
    @Inject(CONSTANTS) _CONST: any
  ) {
    this._CONST = _CONST;
    this._translationFiles = {};
    this._appConfig = APP_CONFIG;
    this._arbeitsbereichConfig = ARBEITSBEREICH_CONFIG;
    this._identifyConfig = IDENTIFY_CONFIG;
    this._baseMapConfig = BASE_MAP_CONFIG;
    this._detailViewConfig = DETAIL_VIEW_CONFIG;
    this._layerContainerConfig = LAYER_CONTAINER_CONFIG;
    this._geoPortaleConfig = GEO_PORTAL_CONFIG;
    this._layerListConfig = LAYER_LIST_CONFIG;
    this._layerListFunctionConfig = LAYER_LIST_FUNCTION_CONFIG;
    this._listViewConfig = LIST_VIEW_CONFIG;
    this._mainMapConfig = MAIN_MAP_CONFIG;
    this._relationshipTablesConfig = RELATIONSHIP_TABLE_CONFIG;
    this._searchConfig = SEARCH_CONFIG;
    this.environment = ENVIRONMENT;
  }

  /* Public functions
   ===================== */
  get dmsBaseUrl(): string {
    return `https://${this.environment.dmsBaseUrl}`;
  }

  get baseUrl(): string {
    this.setRolesEnvironment(this.environment.baseUrl);
    return this.environment.baseUrl;
  }
  get reportsBaseUrl(): string {
    return this.environment.reportsBaseUrl;
  }

  get translationFiles(): any {
    return this._translationFiles;
  }

  get appConfig(): AppConfig {
    return this._appConfig;
  }

  get mainMapConfig(): MainMapConfig {
    return this._mainMapConfig;
  }

  get baseMapConfig(): BaseMapConfig {
    return this._baseMapConfig;
  }

  get layerListConfig(): LayerListConfig {
    return this._layerListConfig;
  }

  get layerListFunctionConfig(): LayerListFunctionConfig {
    return this._layerListFunctionConfig;
  }

  get layerContainerConfig(): LayerContainerConfig {
    return this._layerContainerConfig;
  }

  get listViewConfig(): ListViewConfig {
    return this._listViewConfig;
  }

  get detailViewConfig(): DetailViewConfig {
    return this._detailViewConfig;
  }

  get arbeitsbereichConfig(): ArbeitsbereichConfig {
    return this._arbeitsbereichConfig;
  }

  get identifyConfig(): IdentifyConfig {
    return this._identifyConfig;
  }

  get searchConfig(): SearchConfig {
    return this._searchConfig;
  }

  get relationshipTablesConfig(): RelationshipTableConfig {
    return this._relationshipTablesConfig;
  }

  get geoPortaleConfig(): GeoPortaleConfig {
    return this._geoPortaleConfig;
  }

  getArbeitsbereichConfig(workspace: Enums.Workspace): Arbeitsbereich {
    return this.arbeitsbereichConfig.arbeitsbereiche.find(x => x.id === workspace);
  }

  getDetailViewConfig(workspace: string): DetailViewForm {
    return this.detailViewConfig.detailviews.find(x => x.fkArbeitsbereich === workspace);
  }

  getRolesEnvironment(): string {
    return this._rolesEnvironment;
  }

  setRolesEnvironment(baseUrl: any) {
    const stages = this.roleStages();
    stages.forEach(stage => {
      if (baseUrl.includes(stage.origin)) this._rolesEnvironment = stage.environment;
    });
  }

  private roleStages(): { origin: string; environment: string }[] {
    return [
      {
        origin: this._CONST.ENVIRONMENT.GSHARP_SERVER.GEO_DEV,
        environment: this._CONST.ENVIRONMENT.ROLES.INT,
      },
      {
        origin: this._CONST.ENVIRONMENT.GSHARP_SERVER.GEO_INT,
        environment: this._CONST.ENVIRONMENT.ROLES.INT,
      },
      {
        origin: this._CONST.ENVIRONMENT.GSHARP_SERVER.GEO,
        environment: this._CONST.ENVIRONMENT.ROLES.PROD,
      },
    ];
  }

  /**
   * @function loadAllConfigs
   * @returns {Promise<any>}
   * @description Initialisiere alle Json-Configs aus Assets beim
   * Startup der Applikation. Diese Funktion wird in der app.module.ts
   * ein Mal aufgerufen. Observable Pattern von RXJS zur Startup Zeit
   * nicht verfügbar. Der Loader Mechanismus nimmt deshalb als Return
   * Wert ein Promis.
   */
  public loadAllConfigs(): Promise<any> {
    const _userInfoSoeURL = this.baseUrl + this.appConfig.userUrl;

    const params = new HttpParams().set('f', 'json');
    this._httpClientService
      .get<any>(_userInfoSoeURL, {
        params: params,
        withCredentials: true,
      })
      .subscribe(httpResponse => {
        return httpResponse;
      });

    return Promise.all([this.loadTranslateConfig(this._appConfig.translationConfig)]);
  }

  /**
   * @name getLayerUrl
   * @param id
   * @returns {string}
   * @description gets the url form the layer config based on the id
   */
  public getLayerUrl(id: string): string {
    const layerConfigs: LayerConfig[] = this.layerContainerConfig.layers.filter(
      f => f.properties.id === id
    );
    if (layerConfigs.length !== 1) {
      throw Error(
        'There must be exact one entiry for id=' + id + ' in the layer-container.config.json'
      );
    }
    // get url with extension path
    const url =
      layerConfigs[0].properties.url + (layerConfigs[0].extension ? layerConfigs[0].extension : '');
    return this.fullUrl(url);
  }

  /**
   * @name fullUrl
   * @param url
   * @returns {string}
   * @description Sets base path if needed
   */
  public fullUrl(url: string): string {
    if (!url.toLowerCase().startsWith('http')) {
      url = this.baseUrl + url;
    }
    return url;
  }

  /* Private functions
   ===================== */

  /**
   * @function loadTranslateConfig
   * @param translateConfig Path to the JSON file
   * @returns {Promise<any>}
   * @description get Esri configs from assets
   */
  private loadTranslateConfig(translateConfig: any): Promise<any> {
    let fileDE: any;
    let fileFR: any;
    let fileIT: any;

    const translateDE = new Promise((resolve, reject) => {
      this._httpClientService.get<any>(translateConfig.de.path).subscribe(
        jsonDE => {
          fileDE = jsonDE;
          resolve(true);
        },
        error => {
          reject(this.handleError(error, translateConfig));
        }
      );
    });

    const translateFR = new Promise((resolve, reject) => {
      this._httpClientService.get<any>(translateConfig.fr.path).subscribe(
        jsonFR => {
          fileFR = jsonFR;
          resolve(true);
        },
        error => {
          reject(this.handleError(error, translateConfig));
        }
      );
    });

    const translateIT = new Promise((resolve, reject) => {
      this._httpClientService.get<any>(translateConfig.it.path).subscribe(
        jsonIT => {
          fileIT = jsonIT;
          resolve(true);
        },
        error => {
          reject(this.handleError(error, translateConfig));
        }
      );
    });

    // Wenn alle 3 Sprachen geladen sind, dann in Object Property de/fr/it speichern
    return Promise.all([translateDE, translateFR, translateIT]).then(() => {
      this._translationFiles.de = fileDE;
      this._translationFiles.fr = fileFR;
      this._translationFiles.it = fileIT;

      return true;
    });
  }

  /**
   * @function handleError
   * @param {HttpErrorResponse | any} error
   * @param url
   * @description Handle error from response
   */
  private handleError(error: HttpErrorResponse | any, url?: string) {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof HttpErrorResponse) {
      errMsg = `UMWELT-GIS [Error] : Resource Endpoint '${error.url}' is cannot be found!!!
        Please ensure the path to this config file (defined in environment.ts) is correct.`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    return errMsg;
  }
}
